import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/Button';
import { copyToClipboard } from '@/utils/clipboard';
import { formatJson } from '@/utils/json';
import { showToast } from '@/components/Toast/Toast';
import styles from './DebugPanel.module.css';

interface DebugPanelProps {
  requestPayload: unknown;
  responsePayload: unknown;
  responseHeaders: Record<string, string>;
}

export function DebugPanel({ requestPayload, responsePayload, responseHeaders }: DebugPanelProps) {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleCopy = async (data: unknown) => {
    const success = await copyToClipboard(formatJson(data));
    if (success) {
      showToast('success', t('toast.copySuccess'));
    } else {
      showToast('error', t('toast.copyError'));
    }
  };

  if (!requestPayload && !responsePayload) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <button
        className={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
        aria-expanded={isExpanded}
        aria-controls="debug-content"
      >
        <span className={styles.title}>{t('debug.title')}</span>
        <span className={styles.toggle}>{isExpanded ? '▼' : '▶'}</span>
      </button>

      {isExpanded && (
        <div id="debug-content" className={styles.content}>
          {requestPayload !== null && requestPayload !== undefined && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4 className={styles.sectionTitle}>{t('debug.request')}</h4>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => handleCopy(requestPayload)}
                >
                  {t('common.copy')}
                </Button>
              </div>
              <pre className={styles.code}>{formatJson(requestPayload) as string}</pre>
            </div>
          )}

          {responsePayload !== null && responsePayload !== undefined && (
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h4 className={styles.sectionTitle}>{t('debug.response')}</h4>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => handleCopy(responsePayload)}
                >
                  {t('common.copy')}
                </Button>
              </div>
              <pre className={styles.code}>{formatJson(responsePayload) as string}</pre>
            </div>
          )}

          {Object.keys(responseHeaders).length > 0 && (
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>{t('debug.headers')}</h4>
              <pre className={styles.code}>{formatJson(responseHeaders)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
